import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Clock3, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FormAlert, inputCls, PrimaryButton } from "@/components/FormBits";
import { TopBar } from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import {
  AMOUNT_PRESETS,
  PURPOSE_OPTIONS,
  formatCurrency,
  parseCurrencyToCents,
  todayForDateInput,
} from "@/lib/goals";

const steps = ["Valor", "Objetivo", "Prazo", "Resumo"];
const LONG_AMOUNT_PRESET = 4_000_000;

const amountOptionClass = (selected, spansFullWidth = false) => [
  "min-h-[66px] min-w-0 rounded-xl border-2 px-4 text-lg font-bold whitespace-nowrap",
  "transition-[transform,background-color,border-color] hover:-translate-y-0.5",
  spansFullWidth ? "min-[480px]:col-span-2" : "",
  selected
    ? "border-emerald-700 bg-emerald-700 text-white"
    : "border-amber-200 bg-amber-50 text-emerald-950 hover:border-amber-400",
].join(" ");

const purposeTestId = (option) =>
  option
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replaceAll(" ", "-");

const StepHeader = ({ current }) => (
  <ol data-testid="goal-wizard-steps" className="mb-9 grid grid-cols-4 gap-2">
    {steps.map((label, index) => (
      <li key={label} className="min-w-0">
        <span className={`grid h-9 w-9 place-items-center rounded-full font-bold ${index + 1 <= current ? "bg-emerald-700 text-white" : "bg-amber-100 text-amber-900"}`}>
          {index + 1}
        </span>
        <span className="mt-2 block truncate text-sm font-semibold text-slate-600">{label}</span>
      </li>
    ))}
  </ol>
);

export default function GoalCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [amountCents, setAmountCents] = useState(0);
  const [usingCustomAmount, setUsingCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [customPurpose, setCustomPurpose] = useState("");
  const [deadlineMode, setDeadlineMode] = useState("none");
  const [deadlineDate, setDeadlineDate] = useState("");
  const [deadlineDays, setDeadlineDays] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const customCents = useMemo(() => parseCurrencyToCents(customAmount), [customAmount]);
  const finalCents = usingCustomAmount ? customCents : amountCents;

  const next = () => {
    setError("");
    if (step === 1 && finalCents < 1) {
      setError("Escolha ou informe o valor da sua meta.");
      return;
    }
    if (step === 2 && !purpose) {
      setError("Escolha para que você quer guardar.");
      return;
    }
    if (step === 2 && purpose === "Outro" && !customPurpose.trim()) {
      setError("Escreva para que você quer guardar este dinheiro.");
      return;
    }
    if (step === 3 && deadlineMode === "date" && !deadlineDate) {
      setError("Escolha uma data ou selecione sem prazo.");
      return;
    }
    if (step === 3 && deadlineMode === "days" && Number(deadlineDays) < 1) {
      setError("Informe uma quantidade de dias válida.");
      return;
    }
    setStep((current) => current + 1);
  };

  const create = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await api.post("/goals", {
        amount_cents: finalCents,
        purpose_category: purpose,
        custom_purpose: purpose === "Outro" ? customPurpose : null,
        deadline_mode: deadlineMode,
        deadline_date: deadlineMode === "date" ? deadlineDate : null,
        deadline_days: deadlineMode === "days" ? Number(deadlineDays) : null,
      });
      toast.success("Seu desafio foi criado!");
      navigate(`/metas/${data.id}`, { replace: true });
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  const title = purpose === "Outro" ? customPurpose : purpose;
  const objective = purpose === "Prefiro não informar" ? "Não informado" : title;

  return (
    <div data-testid="goal-create-page" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-3xl px-5 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => (step === 1 ? navigate("/painel") : setStep((current) => current - 1))}
          data-testid="goal-wizard-back-button"
          className="mb-7 inline-flex min-h-[46px] items-center gap-2 rounded-xl px-3 text-lg font-bold text-emerald-800 transition-colors hover:bg-emerald-50"
        >
          <ArrowLeft className="h-5 w-5" /> Voltar
        </button>
        <section className="rounded-3xl border border-amber-200 bg-white/90 p-6 shadow-sm sm:p-10">
          <StepHeader current={step} />
          {step === 1 && (
            <div data-testid="goal-wizard-amount-step" className="fade-up">
              <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Seu desafio começa aqui</p>
              <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950">Quanto você quer guardar?</h1>
              <p className="mt-3 text-lg text-slate-600">Escolha um valor ou escreva o valor que faz sentido para você.</p>
              <div className="mt-8 grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
                {AMOUNT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => {
                      setAmountCents(preset);
                      setCustomAmount("");
                      setUsingCustomAmount(false);
                    }}
                    aria-pressed={!usingCustomAmount && amountCents === preset}
                    data-testid={`amount-preset-${preset / 100}`}
                    className={amountOptionClass(
                      !usingCustomAmount && amountCents === preset,
                      preset >= LONG_AMOUNT_PRESET,
                    )}
                  >
                    {formatCurrency(preset)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setAmountCents(0);
                    setUsingCustomAmount(true);
                  }}
                  aria-pressed={usingCustomAmount}
                  data-testid="amount-preset-other"
                  className={amountOptionClass(usingCustomAmount, true)}
                >
                  Outro valor
                </button>
              </div>
              {usingCustomAmount && (
                <div className="mt-5 fade-up">
                  <label
                    className="block text-lg font-bold text-slate-800"
                    htmlFor="custom-goal-amount"
                  >
                    Qual valor você tem em mente?
                  </label>
                  <input
                    id="custom-goal-amount"
                    data-testid="custom-goal-amount-input"
                    value={customAmount}
                    onChange={(event) => setCustomAmount(event.target.value)}
                    inputMode="decimal"
                    placeholder="Ex.: 3.500 ou 3.500,50"
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
              )}
            </div>
          )}
          {step === 2 && (
            <div data-testid="goal-wizard-purpose-step" className="fade-up">
              <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Dê um sentido ao seu desafio</p>
              <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950">Para que você quer guardar esse dinheiro?</h1>
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {PURPOSE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setPurpose(option)}
                    aria-pressed={purpose === option}
                    data-testid={`purpose-option-${purposeTestId(option)}`}
                    className={`min-h-[60px] rounded-xl border-2 px-3 text-lg font-bold transition-[transform,background-color,border-color] hover:-translate-y-0.5 ${purpose === option ? "border-emerald-700 bg-emerald-700 text-white" : "border-amber-200 bg-amber-50 text-emerald-950 hover:border-amber-400"}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {purpose === "Outro" && (
                <div className="mt-6 fade-up">
                  <label htmlFor="custom-goal-purpose" className="block text-lg font-bold text-slate-800">
                    Conte com suas palavras
                  </label>
                  <input
                    id="custom-goal-purpose"
                    data-testid="custom-goal-purpose-input"
                    value={customPurpose}
                    onChange={(event) => setCustomPurpose(event.target.value)}
                    maxLength={500}
                    placeholder="Ex.: Reforma da cozinha"
                    className={`mt-2 ${inputCls}`}
                  />
                </div>
              )}
            </div>
          )}
          {step === 3 && (
            <div data-testid="goal-wizard-deadline-step" className="fade-up">
              <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Opcional</p>
              <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950">Você quer definir um prazo?</h1>
              <p className="mt-3 text-lg text-slate-600">Um prazo pode ajudar no ritmo, mas sua meta funciona muito bem sem ele.</p>
              <div className="mt-8 grid gap-3">
                {[
                  ["none", "Sem prazo", "Vou guardar no meu ritmo."],
                  ["date", "Escolher uma data", "Quero chegar lá até uma data."],
                  ["days", "Quantidade de dias", "Quero definir quantos dias tenho."],
                ].map(([mode, label, description]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setDeadlineMode(mode)}
                    aria-pressed={deadlineMode === mode}
                    data-testid={`deadline-mode-${mode}`}
                    className={`flex min-h-[76px] items-center gap-4 rounded-xl border-2 p-4 text-left transition-[transform,background-color,border-color] hover:-translate-y-0.5 ${deadlineMode === mode ? "border-emerald-700 bg-emerald-50" : "border-amber-200 bg-white hover:border-amber-400"}`}
                  >
                    {mode === "date" ? <CalendarDays className="h-7 w-7 text-amber-700" /> : mode === "days" ? <Clock3 className="h-7 w-7 text-amber-700" /> : <Sparkles className="h-7 w-7 text-amber-700" />}
                    <span><span className="block text-lg font-bold text-emerald-950">{label}</span><span className="block text-base text-slate-600">{description}</span></span>
                  </button>
                ))}
              </div>
              {deadlineMode === "date" && (
                <label className="mt-6 block text-lg font-bold text-slate-800" htmlFor="goal-deadline-date">
                  Data limite
                    <input id="goal-deadline-date" data-testid="goal-deadline-date-input" type="date" min={todayForDateInput()} value={deadlineDate} onChange={(event) => setDeadlineDate(event.target.value)} className={`mt-2 ${inputCls}`} />
                </label>
              )}
              {deadlineMode === "days" && (
                <label className="mt-6 block text-lg font-bold text-slate-800" htmlFor="goal-deadline-days">
                  Quantos dias?
                  <input id="goal-deadline-days" data-testid="goal-deadline-days-input" type="number" min="1" max="365" value={deadlineDays} onChange={(event) => setDeadlineDays(event.target.value)} inputMode="numeric" placeholder="Ex.: 90" className={`mt-2 ${inputCls}`} />
                </label>
              )}
            </div>
          )}
          {step === 4 && (
            <div data-testid="goal-wizard-summary-step" className="fade-up">
              <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Revise antes de começar</p>
              <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950">Resumo do seu desafio</h1>
              <dl data-testid="goal-summary" className="mt-8 divide-y divide-amber-100 rounded-2xl border border-amber-200 bg-amber-50/60 px-5">
                <div className="flex flex-wrap justify-between gap-2 py-4"><dt className="text-lg text-slate-600">Nome da meta</dt><dd className="text-lg font-bold text-emerald-950">{title === "Prefiro não informar" ? "Minha meta" : title}</dd></div>
                <div className="flex flex-wrap justify-between gap-2 py-4"><dt className="text-lg text-slate-600">Valor total</dt><dd className="text-lg font-bold text-emerald-950">{formatCurrency(finalCents)}</dd></div>
                <div className="flex flex-wrap justify-between gap-2 py-4"><dt className="text-lg text-slate-600">Objetivo</dt><dd className="text-lg font-bold text-emerald-950">{objective}</dd></div>
                <div className="flex flex-wrap justify-between gap-2 py-4"><dt className="text-lg text-slate-600">Prazo</dt><dd className="text-lg font-bold text-emerald-950">{deadlineMode === "date" ? new Date(`${deadlineDate}T12:00:00`).toLocaleDateString("pt-BR") : deadlineMode === "days" ? `${deadlineDays} dias` : "Sem prazo"}</dd></div>
              </dl>
            </div>
          )}
          <FormAlert testId="goal-wizard-error">{error}</FormAlert>
          <div className="mt-8">
            {step < 4 ? (
              <PrimaryButton type="button" onClick={next} data-testid="goal-wizard-next-button">
                Continuar <ArrowRight className="h-5 w-5" />
              </PrimaryButton>
            ) : (
              <PrimaryButton type="button" onClick={create} loading={saving} data-testid="create-goal-submit-button">
                <Check className="h-5 w-5" /> Criar meu desafio
              </PrimaryButton>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}